import { useToast } from '@chakra-ui/react'
import fileDownload from 'js-file-download'
import { createContext, FC, useState } from 'react'
import { useImmer } from 'use-immer'

import followUpFornecedorService from '@service/followUpFornecedor'

import {
  IListagemContext,
  IListagemItem,
  IListagemKpi,
  IListagemParams
} from '@base/@types/providers/FollowUpFornecedor/ListagemProvider'

export const ListagemContext = createContext<IListagemContext>(
  {} as IListagemContext
)

const ListagemProvider: FC = ({ children }) => {
  const toast = useToast()
  const [loading, setLoading] = useState(false)
  const [kpiLoading, setKpiLoading] = useState(false)
  const [query, setQuery] = useState('')
  const [currentyDate, setCurrentyDate] = useState<{
    initialDate: string
    finalDate: string
  }>({ initialDate: '', finalDate: '' })
  const [data, updateData] = useImmer<IListagemItem[]>([])
  const [kpi, setKpi] = useState<IListagemKpi>({
    total: 0,
    emailEnviado: 0,
    submetido: 0,
    aprovados: 0,
    expirados: 0
  })
  const [pagination, updatePagination] = useImmer({
    actualPage: 1,
    rows: 50,
    totalPages: 1,
    totalRows: 0,
    skip: 0
  })
  const [params, updateParams] = useImmer<IListagemParams>({
    fornecedores: [] as any,
    centros: [] as any,
    statusAprovacao: [],
    statusDisparo: [],
    initialDate: '',
    finalDate: '',
    take: 50,
    skip: 0,
    usuarios: [] as any,
    pedidos: [],
    produtos: [] as any
  })

  const buildBody = (p: IListagemParams) => ({
    fornecedores: {
      ids: Array.isArray(p.fornecedores)
        ? (p.fornecedores as any[]).map((o: any) => Number(o.value))
        : (p.fornecedores as any).ids ?? []
    },
    centros: {
      ids: Array.isArray(p.centros)
        ? (p.centros as any[]).map((o: any) => Number(o.value))
        : (p.centros as any).ids ?? []
    },
    statusAprovacao: Array.isArray(p.statusAprovacao)
      ? p.statusAprovacao.map((o: any) =>
          typeof o === 'object' ? Number(o.value) : Number(o)
        )
      : [],
    statusDisparo: Array.isArray(p.statusDisparo)
      ? p.statusDisparo.map((o: any) =>
          typeof o === 'object' ? Number(o.value) : Number(o)
        )
      : [],
    dataInicio: p.initialDate || undefined,
    dataFim: p.finalDate || undefined,
    take: p.take,
    skip: p.skip,
    usuarios: {
      ids: Array.isArray((p as any).usuarios)
        ? ((p as any).usuarios as any[]).map((o: any) => Number(o.value))
        : (p.usuarios as any)?.ids ?? []
    },
    pedidos: (() => {
      const arr = Array.isArray(p.pedidos) ? p.pedidos : []
      const descs = arr.map((o: any) =>
        typeof o === 'object' ? String(o.value) : String(o)
      )
      return descs.length > 0 ? { descricoes: descs } : undefined
    })(),
    produtos: {
      ids: Array.isArray(p.produtos)
        ? (p.produtos as any[]).map((o: any) =>
            typeof o === 'object' ? Number(o.value) : Number(o)
          )
        : (p.produtos as any)?.ids ?? []
    }
  })

  const buildCardsBody = (p: IListagemParams) => ({
    fornecedores: {
      ids: Array.isArray(p.fornecedores)
        ? (p.fornecedores as any[]).map((o: any) => Number(o.value))
        : (p.fornecedores as any).ids ?? []
    },
    centros: {
      ids: Array.isArray(p.centros)
        ? (p.centros as any[]).map((o: any) => Number(o.value))
        : (p.centros as any).ids ?? []
    },
    statusAprovacao: Array.isArray(p.statusAprovacao)
      ? p.statusAprovacao.map((o: any) =>
          typeof o === 'object' ? Number(o.value) : Number(o)
        )
      : [],
    dataInicio: p.initialDate || undefined,
    dataFim: p.finalDate || undefined,
    usuarios: {
      ids: Array.isArray((p as any).usuarios)
        ? ((p as any).usuarios as any[]).map((o: any) => Number(o.value))
        : (p.usuarios as any)?.ids ?? []
    },
    pedidos: (() => {
      const arr = Array.isArray(p.pedidos) ? p.pedidos : []
      const descs = arr.map((o: any) =>
        typeof o === 'object' ? String(o.value) : String(o)
      )
      return descs.length > 0 ? { descricoes: descs } : undefined
    })(),
    produtos: {
      ids: Array.isArray(p.produtos)
        ? (p.produtos as any[]).map((o: any) =>
            typeof o === 'object' ? Number(o.value) : Number(o)
          )
        : (p.produtos as any)?.ids ?? []
    }
  })

  const getKpiCards = async (p: IListagemParams) => {
    setKpiLoading(true)
    try {
      const { data: res } = await followUpFornecedorService.getCards(
        buildCardsBody(p)
      )
      if (res.data) setKpi(res.data)
    } catch {
      // silencioso — cards não bloqueiam a listagem
    } finally {
      setKpiLoading(false)
    }
  }

  const getData = async () => {
    setLoading(true)
    getKpiCards(params)
    try {
      const body = { ...buildBody(params), skip: 0 }
      const { data: res } = await followUpFornecedorService.getListagem(body)
      updateData(res.data ?? [])
      updateParams(draft => {
        draft.skip = 0
      })
      if (res.pagination) updatePagination(res.pagination)
    } catch {
      updateData([])
      toast({
        title: 'Erro ao carregar listagem',
        status: 'error',
        duration: 4000,
        isClosable: true,
        position: 'bottom-right'
      })
    } finally {
      setLoading(false)
    }
  }

  const handleUpdateParams = (name: string, value: any) => {
    updateParams(draft => {
      ;(draft as any)[name] = value
    })
    if (name === 'initialDate') {
      setCurrentyDate(prev => ({ ...prev, initialDate: value }))
    }
    if (name === 'finalDate') {
      setCurrentyDate(prev => ({ ...prev, finalDate: value }))
    }
  }

  const handleExportarListagem = async () => {
    try {
      const body = buildBody(params)
      const { take: _t, skip: _s, ...exportBody } = body
      const { data: blob, headers } =
        await followUpFornecedorService.exportarListagem(exportBody as any)
      const disposition = headers['content-disposition'] || ''
      const match = disposition.match(/filename[^;=\n]*=(.*)/i)
      const filename = match
        ? match[1].replace(/['"]/g, '').trim()
        : `Follow_Up_Fornecedor_${new Date()
            .toLocaleDateString('pt-BR')
            .replace(/\//g, '-')}.csv`
      fileDownload(blob, filename)
    } catch {
      toast({
        title: 'Erro ao exportar',
        status: 'error',
        duration: 4000,
        isClosable: true,
        position: 'bottom-right'
      })
    }
  }

  const handlePageChange = async (page: number) => {
    const newSkip = page * params.take
    setLoading(true)
    try {
      const { data: res } = await followUpFornecedorService.getListagem({
        ...buildBody(params),
        skip: newSkip
      })
      updateData(res.data ?? [])
      if (res.pagination) updatePagination(res.pagination)
      updateParams(draft => {
        draft.skip = newSkip
      })
    } catch {
      updateData([])
      toast({
        title: 'Erro ao carregar listagem',
        status: 'error',
        duration: 4000,
        isClosable: true,
        position: 'bottom-right'
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <ListagemContext.Provider
      value={{
        loading,
        kpiLoading,
        data,
        params,
        pagination,
        kpi,
        query,
        currentyDate,
        getData,
        exportarListagem: handleExportarListagem,
        handleUpdateParams,
        handlePageChange,
        setQuery
      }}
    >
      {children}
    </ListagemContext.Provider>
  )
}

export default ListagemProvider
